import { faRotateLeft, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { saveImageRotation } from 'utils/rotateImage';
import { toastConfig } from 'utils/toastConfig';

export const useImageRotation = (googleFileId?: string | null) => {
  const [rotationDegrees, setRotationDegrees] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRotationDegrees(0);
  }, [googleFileId]);

  const rotateBy = (delta: number) => {
    setRotationDegrees(prev => (((prev + delta) % 360) + 360) % 360);
  };

  const saveRotation = async () => {
    if (!googleFileId || rotationDegrees === 0) return;
    setSaving(true);
    try {
      await saveImageRotation(googleFileId, rotationDegrees);
      setRotationDegrees(0);
      toast.success('Image rotated and saved', { ...toastConfig, autoClose: 2000 });
    } catch (error) {
      const message =
        error instanceof AxiosError
          ? error.response?.data?.errorMessage || 'Failed to save rotated image'
          : 'Failed to save rotated image';
      toast.error(message, { ...toastConfig, autoClose: 3000 });
    } finally {
      setSaving(false);
    }
  };

  return { rotationDegrees, rotateBy, saveRotation, saving };
};

type ImageRotateControlsProps = {
  googleFileId?: string | null;
  rotationDegrees: number;
  saving: boolean;
  onRotate: (delta: number) => void;
  onSave: () => void;
};

export const ImageRotateControls = ({
  googleFileId,
  rotationDegrees,
  saving,
  onRotate,
  onSave
}: ImageRotateControlsProps) => {
  return (
    <div className="d-flex gap-2 flex-wrap">
      <Button
        variant="outline-secondary"
        onClick={() => onRotate(-90)}
        disabled={saving}
        title="Rotate left"
      >
        <FontAwesomeIcon icon={faRotateLeft} className="me-2" />
        Rotate left
      </Button>
      <Button
        variant="outline-secondary"
        onClick={() => onRotate(90)}
        disabled={saving}
        title="Rotate right"
      >
        <FontAwesomeIcon icon={faRotateRight} className="me-2" />
        Rotate right
      </Button>
      {googleFileId && (
        <Button
          variant="primary"
          onClick={onSave}
          disabled={saving || rotationDegrees === 0}
        >
          {saving ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              Saving…
            </>
          ) : (
            'Save rotation'
          )}
        </Button>
      )}
    </div>
  );
};
